import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Mic, Plus, Loader2, Sparkles, AlertCircle, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { dbManager } from '../services/db_manager';
import { AppBackButton } from '../components/AppBackButton';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { AnimatedIcon } from '../components/AnimatedIcon';

interface VoiceDeck {
  id: string;
  user_id: string;
  lang_code: string;
}

export const LANGUAGE_NAMES: Record<string, string> = {
  'pt': 'Português',
  'en': 'Inglês',
  'es': 'Espanhol',
  'fr': 'Francês',
  'it': 'Italiano',
  'de': 'Alemão',
  'ja': 'Japonês',
  'zh': 'Mandarim',
  'ru': 'Russo',
  'ko': 'Coreano',
  'hi': 'Hindi',
  'ar': 'Árabe',
  'tr': 'Turco',
  'nl': 'Holandês',
  'pl': 'Polonês'
};

const ActiveVoicePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { practiceLang } = useData();
  const navigate = useNavigate();
  const [decks, setDecks] = useState<VoiceDeck[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  
  useEffect(() => {
    fetchDecks();
  }, [user]);

  const fetchDecks = async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const response = await dbManager.from('active_voice_decks', user.id).select();
      const decksData = Array.isArray(response) ? response : response?.data;
      if (decksData) {
        setDecks(decksData as VoiceDeck[]);
      }
    } catch (e) {
      console.error("Erro ao buscar decks:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewConversation = () => {
    navigate('/active-voice/new');
  };

  const getLanguageName = (code: string) => {
    return LANGUAGE_NAMES[code] || code.toUpperCase();
  };

  if (isLoading && decks.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-tintd-sky animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-full flex flex-col bg-slate-50 dark:bg-slate-950/40 p-4 md:p-8">
      <div className="mb-6">
        <AppBackButton 
          to="/study-hub"
        />
      </div>

      <div className="mb-10 space-y-6">
        <StudyPageHeader tourKey="active_voice" title={String(t('active_voice.hub_title'))} />
        <button
          data-tour="av-new"
          onClick={handleCreateNewConversation}
          disabled={isLoading}
          className="group h-12 pl-5 pr-2 rounded-full bg-primary text-slate-900 text-sm font-medium inline-flex items-center gap-3 cursor-pointer hover:opacity-90 disabled:opacity-50"
        >
          {t('active_voice.start_new')}
          <span className="w-8 h-8 rounded-full bg-surface/15 flex items-center justify-center transition-transform group-hover:rotate-90">
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </span>
        </button>
      </div>

      {errorMsg && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-tint-rose dark:bg-tint-rose/30 text-bad dark:text-bad p-4 rounded-2xl mb-8 flex items-center gap-3 font-medium border border-tint-rose dark:border-tint-rose"
        >
          <AlertCircle size={20} />
          {errorMsg}
        </motion.div>
      )}

      {decks.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-8 py-16 sm:p-16 bg-surface rounded-[32px] border border-black/[0.06] dark:border-white/[0.06] text-center">
          <div className="w-20 h-20 bg-tint-lavender rounded-full flex items-center justify-center text-ink mb-6">
            <AnimatedIcon motion="pop" active><Sparkles size={30} strokeWidth={1.5} /></AnimatedIcon>
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-light tracking-[-0.04em] text-ink mb-3">{t('active_voice.no_active_conversation')}</h3>
          <p className="text-muted text-sm leading-relaxed max-w-sm">{t('active_voice.no_active_conversation_desc')}</p>
        </div>
      ) : (
        <div data-tour="av-rooms" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {decks.map((deck, di) => (
            <motion.div
              key={deck.id}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(`/active-voice/${deck.id}`)}
              className={`${['bg-tint-sky','bg-tint-sage','bg-tint-peach','bg-tint-lavender','bg-tint-butter','bg-tint-rose'][di % 6]} text-ink p-6 rounded-[32px] cursor-pointer flex flex-col justify-between aspect-square group`}
            >
              <div className="w-16 h-16 rounded-full bg-surface/70 text-ink flex items-center justify-center transition-transform duration-500 group-hover:rotate-[8deg]">
                <span className="font-mono text-lg uppercase tracking-[0.06em]">{deck.lang_code}</span>
              </div>
              
              <div>
                <h3 className="font-display text-2xl font-light tracking-[-0.04em] text-ink mb-1">
                  {getLanguageName(deck.lang_code)}
                </h3>
                <p className="text-[10px] text-ink/60 uppercase font-mono tracking-[0.16em]">
                  {t('active_voice.practice_room')}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveVoicePage;
